import Alpine from "alpinejs";
import persist from "@alpinejs/persist";
import slider from "./scripts/Slider";
import productFormDesktop from "./scripts/ProductFormDesktop";
import productFormMobile from "./scripts/ProductFormMobile";
import cart from "./scripts/cart";
import cartNotice from "./scripts/CartNotice";
import filterDrawer from "./scripts/FilterDrawer";
import cartCount from "./scripts/CartCount";
import menu from "./scripts/Menu";
import menuMobile from "./scripts/menuMobile";
import intersect from "@alpinejs/intersect";
import sticky from "alpinejs-sticky";
import login from "./scripts/login";
import search from "./scripts/search";
import variantPicker from "./scripts/variantPicker";
import PdpBtnDesktop from "./scripts/PdpBtnDesktop";

Alpine.plugin(sticky);
Alpine.plugin(intersect);

Alpine.data("Slider", slider);
Alpine.data("productformdesktop", productFormDesktop);
Alpine.data("productformmobile", productFormMobile);
Alpine.data("cart", cart);
Alpine.data("cartNotice", cartNotice);
Alpine.data("filterDrawer", filterDrawer);
Alpine.data("cartCount", cartCount);
Alpine.data("menu", menu);
Alpine.data("menuMobile", menuMobile);
Alpine.data("login", login);
Alpine.data("search", search);
Alpine.data("variantPicker", variantPicker);
Alpine.data("PdpBtnDesktop", PdpBtnDesktop);

/**
 * Slider module
 */

const debounce = (func, wait, immediate = true) => {
  let timeout;
  return () => {
    const context = this;
    const args = arguments;
    const callNow = immediate && !timeout;
    clearTimeout(timeout);
    timeout = setTimeout(function () {
      timeout = null;
      if (!immediate) {
        func.apply(context, args);
      }
    }, wait);
    if (callNow) func.apply(context, args);
  };
};

/**
 * Append the child element and wait for the parent's
 * dimensions to be recalculated
 * See https://stackoverflow.com/a/66172042/11784757
 */
const appendChildAwaitLayout = (parent, element) => {
  return new Promise((resolve, _) => {
    const resizeObserver = new ResizeObserver((entries, observer) => {
      observer.disconnect();
      resolve(entries);
    });
    resizeObserver.observe(element);
    parent.appendChild(element);
  });
};

document.addEventListener("alpine:init", () => {
  Alpine.data(
    "Marquee",
    ({ speed = 1, spaceX = 0, dynamicWidthElements = false }) => ({
      dynamicWidthElements,
      async init() {
        if (this.dynamicWidthElements) {
          const images = this.$el.querySelectorAll("img");
          // If there are any images, make sure they are loaded before
          // we start cloning them, since their width might be dynamically
          // calculated
          if (images) {
            await Promise.all(
              Array.from(images).map((image) => {
                return new Promise((resolve, _) => {
                  if (image.complete) {
                    resolve();
                  } else {
                    image.addEventListener("load", () => {
                      resolve();
                    });
                  }
                });
              })
            );
          }
        }

        // Store the original element so we can restore it on screen resize later
        this.originalElement = this.$el.cloneNode(true);
        const originalWidth = this.$el.scrollWidth + spaceX * 4;
        // Required for the marquee scroll animation
        // to loop smoothly without jumping
        this.$el.style.setProperty("--marquee-width", originalWidth + "px");
        this.$el.style.setProperty(
          "--marquee-time",
          ((1 / speed) * originalWidth) / 100 + "s"
        );
        this.resize();
        // Make sure the resize function can only be called once every 100ms
        // Not strictly necessary but stops lag when resizing window a bit
        window.addEventListener(
          "resize",
          debounce(this.resize.bind(this), 100)
        );
      },
      async resize() {
        // Reset to original number of elements
        this.$el.innerHTML = this.originalElement.innerHTML;

        // Keep cloning elements until marquee starts to overflow
        let i = 0;
        while (this.$el.scrollWidth <= this.$el.clientWidth) {
          if (this.dynamicWidthElements) {
            // If we don't give this.$el time to recalculate its dimensions
            // when adding child nodes, the scrollWidth and clientWidth won't
            // change, thus resulting in this while loop looping forever
            await appendChildAwaitLayout(
              this.$el,
              this.originalElement.children[i].cloneNode(true)
            );
          } else {
            this.$el.appendChild(
              this.originalElement.children[i].cloneNode(true)
            );
          }
          i += 1;
          i = i % this.originalElement.childElementCount;
        }

        // Add another (original element count) of clones so the animation
        // has enough elements off-screen to scroll into view
        let j = 0;
        while (j < this.originalElement.childElementCount) {
          this.$el.appendChild(
            this.originalElement.children[i].cloneNode(true)
          );
          j += 1;
          i += 1;
          i = i % this.originalElement.childElementCount;
        }
      },
    })
  );
});

window.Alpine = Alpine;
Alpine.plugin(persist);

Alpine.start();

let Grebify = {
  updateQuantity(line, qty) {
    fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ quantity: qty, line: line }),
    })
      .then((response) => response.json())
      .then((data) => {
        // fire javascript event on window
        window.dispatchEvent(new Event("cart-updated"));
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  },

  noScroll(active = true) {
    const body = document.querySelector("body");
    if (active) {
      body.classList.add("no-scroll");
    } else {
      body.classList.remove("no-scroll");
    }
  },

  getCSSVariable(variable) {
    var rs = getComputedStyle(r);
    return rs.getPropertyValue(variable);
  },

  setCSSVariable(variable, value) {
    r.style.setProperty(variable, value);
  },
};

window.Grebify = Grebify;

var r = document.querySelector(":root");

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement("div");
          html.innerHTML = text;
          const recommendations = html.querySelector("product-recommendations");

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (
            !this.querySelector("slideshow-component") &&
            this.classList.contains("complementary-products")
          ) {
            this.remove();
          }

          if (html.querySelector(".grid__item")) {
            this.classList.add("product-recommendations--loaded");
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), {
      rootMargin: "0px 0px 400px 0px",
    }).observe(this);
  }
}

customElements.define("product-recommendations", ProductRecommendations);

/* Banner animation */

const banner = document.querySelector(".variant-banner");
const bannerItems = document.querySelectorAll(".banner-item");
const scrollInterval = 4000;

let currentItemIndex = 0;
let scrollTimer;

// Immediately display the first item without delay
bannerItems.forEach((item, index) => {
  if (index === currentItemIndex) {
    item.style.opacity = "1";
    item.style.transform = "translateY(0)";
  } else {
    item.style.opacity = "0";
    item.style.transform = "translateY(-100%)";
  }
});

function scrollBannerItems() {
  // Set the current item as active and reset opacity and transform of other items
  bannerItems.forEach((item, index) => {
    if (index === currentItemIndex) {
      // console.log(currentItemIndex);
      item.style.opacity = "1";
      item.style.transform = "translateY(0)";
    } else {
      item.style.opacity = "0";
      item.style.transform = "translateY(-100%)";

      var delayInMilliseconds = 500;
      setTimeout(function () {
        item.style.transform = "translateY(100%)";
      }, delayInMilliseconds);
    }
  });

  // Increment the current item index or reset to 0 if at the end
  if (currentItemIndex === bannerItems.length - 1) {
    currentItemIndex = 0;
  } else {
    currentItemIndex++;
  }
}

// Start the scrolling timer
scrollTimer = setInterval(scrollBannerItems, scrollInterval);
